import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import { debounce } from "instantsearch.js/es/lib/utils";

import { connectGeoSearch } from "instantsearch.js/es/connectors";

export default function mapboxGeoSearchWidget(widgetParams, dispose) {
    /**
     * The state reference must be accessible from this scope to perform unmount
     **/
    const widgetState = {
        map: null,
        loaded: false,
        markers: {},
        isMarkerOnMap: {},
    };

    const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
            if (widgetState.map) {
                widgetState.map.resize();
            }
        }
    });

    const containerElement = document.querySelector(widgetParams.container);

    const hitsToGeoJson = (hits) => {
        return {
            type: "FeatureCollection",
            features: hits.flatMap((hit) => {
                if (!hit._geoloc) {
                    return [];
                }

                return [
                    {
                        id: hit.id,
                        type: "Feature",
                        geometry: {
                            type: "Point",
                            coordinates: [hit._geoloc.lng, hit._geoloc.lat],
                        },
                        properties: hit,
                    },
                ];
            }),
        };
    };

    const featureToPopup = (feature) => {
        const { createPopup, popup } = widgetParams;

        const element = createPopup(feature);

        const _popup = new mapboxgl.Popup({
            offset: 25,
            anchor: "left",
            closeButton: false,
            ...popup,
        }).setDOMContent(element);

        return {
            element,
            popup: _popup,
        };
    };

    const featureToMarker = (feature) => {
        const { createMarker, createPopup } = widgetParams;

        const root = createMarker(feature);

        const marker = new mapboxgl.Marker(root).setLngLat(feature.geometry.coordinates);

        if (createPopup) {
            const { element: popupElement, popup } = featureToPopup(feature);

            marker.setPopup(popup);

            const togglePopup = (marker, popup, open = null) => {
                if ((open === true && popup.isOpen()) || (open === false && !popup.isOpen())) {
                    return;
                }

                marker.togglePopup();
            };

            root.addEventListener("mouseenter", () => togglePopup(marker, popup, true));
            root.addEventListener("mouseleave", () => togglePopup(marker, popup, false));

            popupElement.addEventListener("mouseenter", () => togglePopup(marker, popup, true));
            popupElement.addEventListener("mouseleave", () => togglePopup(marker, popup, false));
        }

        return marker;
    };

    const addHits = (hits) => {
        const geojson = hitsToGeoJson(hits);

        widgetState.map.addSource("hits", {
            type: "geojson",
            data: geojson,
            cluster: true,
            clusterMaxZoom: 14, // Max zoom to cluster points on
            clusterRadius: 50, // Radius of each cluster when clustering points (defaults to 50)
        });

        widgetState.map.addLayer({
            id: "clusters",
            type: "circle",
            source: "hits",
            filter: ["has", "point_count"],
            paint: {
                // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
                // with three steps to implement three types of circles:
                //   * Blue, 20px circles when point count is less than 100
                //   * Yellow, 30px circles when point count is between 100 and 750
                //   * Pink, 40px circles when point count is greater than or equal to 750
                "circle-color": "#101828",
                "circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40],
                "circle-stroke-color": "#fff",
                "circle-stroke-width": 5,
            },
        });

        widgetState.map.addLayer({
            id: "cluster-count",
            type: "symbol",
            source: "hits",
            filter: ["has", "point_count"],
            layout: {
                "text-field": "{point_count_abbreviated}",
                "text-font": ["Arial Unicode MS Bold"],
                "text-size": 12,
            },
            paint: {
                "text-color": "#fff",
            },
        });

        widgetState.map.addLayer({
            id: "unclustered-point",
            type: "circle",
            source: "hits",
            filter: ["!", ["has", "point_count"]],
            paint: {
                "circle-color": "#101828",
                "circle-radius": 8,
                "circle-stroke-color": "#fff",
                "circle-stroke-width": 4,
            },
        });

        widgetState.map.on("click", "clusters", (e) => {
            const features = widgetState.map.queryRenderedFeatures(e.point, {
                layers: ["clusters"],
            });

            const clusterId = features[0].properties.cluster_id;

            widgetState.map.getSource("hits").getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err) return;

                widgetState.map.easeTo({
                    center: features[0].geometry.coordinates,
                    zoom: zoom,
                });
            });
        });
    };

    /**
     * This method is debounced because updating the mapbox source is not immediate
     * Debounce act like a timeout
     */
    const updateMarkers = debounce(() => {
        if (!widgetParams.createMarker) {
            return;
        }

        // all the points dispolayed on map
        const features = widgetState.map.queryRenderedFeatures(null, {
            layers: ["unclustered-point"],
        });

        /**
         * This dictionary will store all marker elements
         * Marker add added or removed from the map but we do not destroy them
         */
        const markers = widgetState.markers;

        /**
         * id : bool
         * Represent all the markers that are currently displayed on map
         */
        const wasMarkerOnMap = widgetState.isMarkerOnMap;

        /**
         * id : bool
         * Represent all the markers that should be displayed on map
         */
        const isMarkerOnMap = {};

        features.forEach((feature) => {
            const id = feature.properties.id;

            isMarkerOnMap[id] = true;

            // marker exists
            if (markers[id]) {
                if (wasMarkerOnMap[id]) {
                    // marker exists and is already on map
                    // when zooming the marker position might not match perfectly with the coordinates because of coordinates to DOM approximation
                    // so we refresh the DOM position
                    markers[id].setLngLat(feature.geometry.coordinates);
                } else {
                    markers[id].addTo(widgetState.map);
                }
            } else {
                // marker does not exists at all
                markers[id] = featureToMarker(feature);
                markers[id].addTo(widgetState.map);
            }
        });

        for (const id in markers) {
            if (!isMarkerOnMap[id]) {
                markers[id].remove();
            }
        }

        widgetState.isMarkerOnMap = isMarkerOnMap;
        widgetState.markers = markers;
    }, 100);

    const updateHits = debounce((hits) => {
        const source = widgetState.map.getSource("hits");

        if (source) {
            const geojson = hitsToGeoJson(hits);

            source.setData(geojson);

            void updateMarkers();
        }
    }, 400);

    const boundsToRefine = (bounds) => {
        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();

        return {
            northEast: {
                lat: northEast.lat,
                lng: Math.min(180, Math.max(-180, northEast.lng)),
            },
            southWest: {
                lat: southWest.lat,
                lng: Math.min(180, Math.max(-180, southWest.lng)),
            },
        };
    };

    const refineToBounds = (refine) => {
        if (refine) {
            return [
                refine.southWest.lng,
                refine.southWest.lat,
                refine.northEast.lng,
                refine.northEast.lat,
            ];
        }

        return undefined;
    };

    const render = (renderOptions, isFirstRendering) => {
        const {
            items,
            position,
            currentRefinement,
            refine,
            sendEvent,
            clearMapRefinement,
            isRefinedWithMap,
            toggleRefineOnMapMove,
            isRefineOnMapMove,
            setMapMoveSinceLastRefine,
            hasMapMoveSinceLastRefine,
            widgetParams,
        } = renderOptions;

        const _refine = () => {
            if (isRefineOnMapMove()) {
                refine(boundsToRefine(widgetState.map.getBounds()));
            }
        };

        if (isFirstRendering) {
            // allow the state to be accessed by reference outside of the widget
            widgetParams.state = widgetState;

            if (widgetParams.mapboxToken) {
                mapboxgl.accessToken = widgetParams.mapboxToken;
            }

            widgetState.map =
                widgetParams.map ||
                new mapboxgl.Map({
                    container: containerElement,
                    style: "mapbox://styles/mapbox/streets-v12",
                    center: [2.35183, 48.85658],
                    zoom: 3,
                    projection: "mercator",
                    bounds: refineToBounds(currentRefinement),
                    ...widgetParams.mapOptions,
                });

            if (widgetParams.enableGeocoder) {
                const geocoder = new MapboxGeocoder();

                geocoder.accessToken = mapboxgl.accessToken;
                geocoder.mapboxgl = mapboxgl;
                geocoder.marker = false;

                widgetState.map.addControl(geocoder, "top-left");
            }

            widgetState.map.on("load", () => {
                resizeObserver.observe(containerElement);

                widgetState.map.on("mouseenter", "clusters", () => {
                    widgetState.map.getCanvas().style.cursor = "pointer";
                });
                widgetState.map.on("mouseleave", "clusters", () => {
                    widgetState.map.getCanvas().style.cursor = "";
                });

                widgetState.map.on("moveend", () => {
                    setMapMoveSinceLastRefine();

                    _refine();
                });

                /**
                 * On first render items is empty
                 */
                addHits(items);

                //Fly to user position
                if (widgetParams.enableAutofly && navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                        ({ coords: { longitude, latitude } }) => {
                            widgetState.map.jumpTo({
                                center: [longitude, latitude],
                            });
                        },
                    );
                }

                _refine();

                widgetState.loaded = true;
            });
        }

        if (widgetState.loaded) {
            void updateHits(items);
        }
    };

    const unmount = () => {
        resizeObserver.unobserve(containerElement);

        widgetState.map.remove();

        widgetState.map = null;
        widgetState.loaded = false;
        widgetState.markers = {};
        widgetState.isMarkerOnMap = {};

        if (dispose) {
            dispose();
        }
    };

    return connectGeoSearch(render, unmount)(widgetParams);
}
